import React, { useEffect, useRef, useState } from 'react';

import { apiRequest, apiStream } from './api';
import { appendCappedLog, followLogsUrl, initialReplicaLogPhase, replicaLogAction, ReplicaLogPhase } from './logs';

import { ReplicaEntry } from './types';

const POLL_INTERVAL_MS = 1000;
const BOTTOM_THRESHOLD_PX = 24;

export function ReplicaLogs({
    token,
    replica: initialReplica,
    onClose,
}: {
    token: string;
    replica: ReplicaEntry;
    onClose: () => void;
}) {
    const [replica, setReplica] = useState(initialReplica);
    const [text, setText] = useState('');
    const [error, setError] = useState('');
    const [phase, setPhase] = useState<ReplicaLogPhase>(() => initialReplicaLogPhase(initialReplica));
    const [follow, setFollow] = useState(true);
    const preRef = useRef<HTMLPreElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const action = replicaLogAction(replica);

    useEffect(() => {
        cardRef.current?.scrollIntoView({ block: 'nearest' });
    }, []);

    useEffect(() => {
        if (action !== 'wait') return;
        let cancelled = false;
        let timer: number | undefined;
        const poll = async () => {
            try {
                const next = await apiRequest<ReplicaEntry>(token, 'replicas/get', {
                    replica_id: replica.id,
                });
                if (cancelled) return;
                setReplica(next);
                if (replicaLogAction(next) === 'wait') {
                    timer = window.setTimeout(poll, POLL_INTERVAL_MS);
                }
            } catch (reason) {
                if (!cancelled) {
                    setError(String(reason));
                    setPhase('ended');
                }
            }
        };
        timer = window.setTimeout(poll, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            if (timer !== undefined) window.clearTimeout(timer);
        };
    }, [action, replica.id, token]);

    useEffect(() => {
        if (action !== 'ended') return;
        setPhase('ended');
        if (!replica.container_id) {
            const detail = [replica.termination_reason, replica.termination_message].filter(Boolean).join(': ');
            setError(`Replica ${replica.status} before its container was created${detail ? ` (${detail})` : ''}`);
        } else if (!replica.logs_url) {
            setError('Logs are not available for this replica');
        }
    }, [
        action,
        replica.container_id,
        replica.logs_url,
        replica.status,
        replica.termination_message,
        replica.termination_reason,
    ]);

    useEffect(() => {
        if (action !== 'stream' || !replica.logs_url) return;
        const controller = new AbortController();
        setPhase('streaming');
        setError('');
        void apiStream(token, followLogsUrl(replica.logs_url), {
            signal: controller.signal,
            onChunk: (chunk) => setText((current) => appendCappedLog(current, chunk)),
        })
            .then(() => {
                if (!controller.signal.aborted) setPhase('ended');
            })
            .catch((reason) => {
                if (!controller.signal.aborted) {
                    setError(String(reason));
                    setPhase('ended');
                }
            });
        return () => controller.abort();
    }, [action, replica.logs_url, token]);

    useEffect(() => {
        const element = preRef.current;
        if (element && follow) element.scrollTop = element.scrollHeight;
    }, [text, follow]);

    const handleScroll = () => {
        const element = preRef.current;
        if (!element) return;
        setFollow(element.scrollHeight - element.scrollTop - element.clientHeight < BOTTOM_THRESHOLD_PX);
    };

    return (
        <div className="card" ref={cardRef}>
            <div className="toolbar">
                <h2>Live logs: {replica.id}</h2>
                <span className="muted">{replica.status}</span>
                <button onClick={onClose}>Close</button>
            </div>
            {phase === 'waiting' && <p className="muted">Waiting for the replica container...</p>}
            {error && <p className="error">{error}</p>}
            {phase === 'ended' && !error && <p className="muted">Log stream ended.</p>}
            <pre className="logs" ref={preRef} onScroll={handleScroll}>
                {text}
            </pre>
        </div>
    );
}
